import type {Context, NodePatchResult} from '../types';
import {identifier} from '@packages/systems/wfdl-Qualified';
import {isSymbolRoot} from '@packages/systems/core/utils/DomTypeUtils';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {parseDefaultData} from '@packages/systems/core/render';
import {parseCollectionListPropTypeName} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';
import {
  getCollectionListFilterPropType,
  getCollectionListCuratedPropType,
  getCollectionListSortPropType,
} from '@packages/systems/dynamo/utils/CollectionListPropTypes';
import {
  getSystem,
  type Type,
  Types,
  withSystem,
  Plugins,
  Expressions,
  stringifyExpression,
} from '@packages/systems/wfdl';
import {
  EDistinct,
  EFunction,
  ERecord,
  type Expression,
} from '@packages/systems/wfdl-ast';
import type {CollectionListSupportedProp} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

/**
 * Adds or repairs defaultData on all component roots.
 * Builds defaultData from the component dataType using plugins.
 * Triggers when defaultData is absent or when its record keys differ from the dataType's fields.
 */

const DEFAULT_DATA_PARAM = identifier('context');

export default function patchDefaultDataFromDataType(
  context: Context
): NodePatchResult {
  const {jsonNode} = context.node;
  const plugins = context.extraContext?.plugins;

  const logData = {
    msg: 'Backfill or repair component root defaultData from declared dataType',
    meta: {nodeId: jsonNode._id},
  };

  const isRoot = isSymbolRoot(jsonNode.data);
  const hasPlugins = Boolean(plugins);
  if (!isRoot || !hasPlugins) {
    return {
      needed: false,
      logData,
      apply: () => {},
    };
  }

  const dataType = parseDataType(jsonNode.dataType);
  const rawDefaultData = jsonNode.data?.sym?.defaultData;
  const alreadyHasDefaultData = Boolean(rawDefaultData);
  const mismatch =
    alreadyHasDefaultData &&
    hasPropertyMismatch(parseDefaultData(rawDefaultData), dataType);
  const needed = !alreadyHasDefaultData || mismatch;

  return {
    needed,
    logData,
    apply: () => {
      if (!needed || !plugins || !jsonNode.data?.sym) return;

      const body: Record<string, Expression> = {};

      const fields = dataType ? Types.getRecordFields(dataType) : null;
      if (fields) {
        for (const [propId, propType] of Object.entries(fields)) {
          body[propId] =
            getDefaultCollectionListPropertyExpression(propType, plugins) ??
            getCurrentOrDefaultExpression(plugins)(propType);
        }
      }

      // Matches the Designer's stored form, which avoids Mongo's nesting limit.
      jsonNode.data.sym.defaultData = stringifyExpression(
        EFunction({
          param: DEFAULT_DATA_PARAM,
          body: ERecord(body),
        })
      );
    },
  };
}

const hasPropertyMismatch = (
  defaultData: Expression | undefined,
  dataType: Type | undefined
): boolean => {
  const existingKeys = getDefaultDataRecordKeys(defaultData);
  const dataTypeKeys = dataType
    ? getDataTypeRecordKeys(dataType)
    : new Set<string>();

  if (existingKeys.size !== dataTypeKeys.size) return true;
  for (const key of dataTypeKeys) {
    if (!existingKeys.has(key)) return true;
  }
  return false;
};

const getDataTypeRecordKeys = (dataType: Type): Set<string> => {
  const fields = Types.getRecordFields(dataType);
  if (!fields) return new Set();

  return new Set(Object.keys(fields));
};

const getDefaultDataRecordKeys = (
  defaultData: Expression | undefined
): Set<string> => {
  if (!defaultData) return new Set();
  const fn = Expressions.getFunction(defaultData);
  if (!fn) return new Set();
  const fields = Expressions.getRecord(fn.body);
  if (!fields) return new Set();
  return new Set(Object.keys(fields));
};

const parseDataType = (raw: string | Type | undefined): Type | undefined => {
  if (!raw) return undefined;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

function getDefaultCollectionListPropertyExpression(
  fieldType: Type,
  plugins: Plugins.PluginsType
): Expression | undefined {
  const name = Types.getTypeConstructor(fieldType);
  if (!name) {
    return;
  }

  const parsed = parseCollectionListPropTypeName(name);
  if (!parsed) {
    return;
  }

  const innerType = COLLECTION_LIST_TYPES[parsed.propType]();

  // We have to carry the default from the property data type to the generated type
  // so that getCurrentOrDefaultExpression can convert it to an expression.
  const defaultValue = getSystem(fieldType)?.defaultValue;
  const innerTypeWithDefault = defaultValue
    ? withSystem({defaultValue}, innerType)
    : innerType;

  return EDistinct({
    name,
    value: getCurrentOrDefaultExpression(plugins)(innerTypeWithDefault),
  });
}

const COLLECTION_LIST_TYPES: Record<CollectionListSupportedProp, () => Type> = {
  Filter: getCollectionListFilterPropType,
  Curated: getCollectionListCuratedPropType,
  Sort: getCollectionListSortPropType,
};
